import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { REPORTING_ERROR_HANDLER } from './stale-build-reload';

/** Nothing is reported without a DSN, and the local-only environments ship an empty one. */
const enabled = (): boolean => Boolean(environment.sentry.dsn);

/**
 * Starts the SDK before bootstrap — early enough to catch errors thrown while the app is
 * still coming up, which is exactly when the interesting ones happen.
 *
 * Kept out of `main.ts` so the DSN gate lives next to the providers that depend on it.
 */
export function initSentry(): void {
  if (!enabled()) {
    return;
  }

  Sentry.init({
    dsn: environment.sentry.dsn,
    // The Sentry environment picker's label — 'Demo' or 'Production'. Its own field rather
    // than `environment.name`, which reads 'wds'/'prod' and is already spoken for elsewhere.
    environment: environment.sentry.environment,
    release: environment.appVersion,
    // The Angular-specific one: it names spans after the parametrised route rather than
    // the URL, so `/rides/:id` stays one entry instead of one per ride. Paired with the
    // `TraceService` that `provideSentry()` builds — that is what turns each router
    // navigation into its own transaction; this integration alone only covers the pageload.
    integrations: [Sentry.browserTracingIntegration()],
    // `tracePropagationTargets` is deliberately left at its default, which attaches
    // `sentry-trace`/`baggage` only to same-origin requests. Every API host here is
    // cross-origin and answers a fixed `allowedHeaders` list (see server_admin_api.js in
    // wetaxi_core) that has neither header in it, so widening this would fail the CORS
    // preflight on every call. Distributed tracing needs the backends to allow them first.
    tracesSampleRate: environment.sentry.tracesSampleRate,
    dataCollection: {},
  });
}

/**
 * The DI half of the SDK: errors Angular catches are reported, and router navigations are
 * traced.
 *
 * `ErrorHandler` itself is not provided here — `provideStaleBuildReload()` owns it, and
 * Angular keeps only the last provider for a token, so registering Sentry's handler directly
 * would drop the stale-chunk recovery. It hands off through `REPORTING_ERROR_HANDLER` instead.
 */
export function provideSentry(): EnvironmentProviders {
  if (!enabled()) {
    return makeEnvironmentProviders([]);
  }

  return makeEnvironmentProviders([
    { provide: REPORTING_ERROR_HANDLER, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    // `TraceService` instruments the router from its constructor, so it has to be built even
    // though nothing injects it.
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ]);
}
